import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
} from "../components";
import { menuService } from "../services";
import { buildMenuHierarchy, hasChildren, slugify } from "../utils/menuHelpers";
import rektorPhoto from "../assets/images/faces/rektor.png";
import ketuaPhoto from "../assets/images/faces/ketua.jpg";

function Beranda() {
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [hierarchicalMenus, setHierarchicalMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState({});

  // Fetch menu data from API
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        setLoading(true);
        const response = await menuService.getActiveMenus();
        // Assuming API returns data in response.data or response.menus
        const menuData = response.data || response.menus || response;
        
        if (Array.isArray(menuData)) {
          setMenus(menuData);
          // Build hierarchical structure
          const hierarchy = buildMenuHierarchy(menuData);
          setHierarchicalMenus(hierarchy);
        } else {
          setMenus([]);
          setHierarchicalMenus([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching menus:', err);
        setError('Gagal memuat data menu. Silakan coba lagi nanti.');
        setMenus([]);
        setHierarchicalMenus([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMenus();
  }, []);

  // Toggle submenu visibility
  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Get logo path helper
  const getLogoPath = (logoFilename) => {
    try {
      return new URL(`../assets/images/logo/${logoFilename}`, import.meta.url).href;
    } catch (error) {
      // Fallback to default logo if image not found
      return new URL(`../assets/images/logo/logo.png`, import.meta.url).href;
    }
  };

  // Render menu item with submenu support
  const renderMenuItem = (menu) => {
    const hasSubmenus = menu.children && menu.children.length > 0;
    const isExpanded = expandedMenus[menu.id];

    // Handle menu click
    const handleMenuClick = () => {
      if (hasSubmenus) {
        // Navigate to submenu page with slug
        const menuSlug = slugify(menu.nama_aplikasi);
        navigate(`/menu/${menuSlug}/${menu.id}`);
      } else {
        // Open URL directly
        window.open(menu.url, menu.target || '_blank');
      }
    };

    return (
      <div key={menu.id} className="col-12 col-md-6 col-lg-4 d-flex mb-1">
        <Card className="w-100 mb-3 border-1 shadow-sm">
          <CardBody>
            {/* Parent Menu */}
            <div 
              className="d-flex align-items-center"
              style={{ cursor: 'pointer' }}
              onClick={handleMenuClick}
            >
              <div className="flex-shrink-0 me-3">
                <img 
                  src={getLogoPath(menu.logo)} 
                  alt={`Logo ${menu.nama_aplikasi}`}
                  className="rounded"
                  style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.src = getLogoPath('logo.png');
                  }}
                />
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-1 d-flex align-items-center justify-content-between">
                  <span>{menu.nama_aplikasi}</span>
                  {hasSubmenus && (
                    <i className="bi bi-arrow-right-circle text-muted"></i>
                  )}
                </h5>
                <p className="text-muted mb-0 small">{menu.deskripsi}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  };

  return (
    <>
      <div className="page-content mt-4">
        {/* Baris Pertama - 2 Card dengan Photo dan Sambutan */}
        <section className="row my-2 px-2 py-0">
          <div className="col-12 col-lg-6 d-flex">
            <Card className="w-100 border-1 shadow-sm">
              <CardBody>
                <div className="row align-items-center">
                  <div className="col-12 col-md-4 mb-3 mb-md-0">
                    <img 
                      src={rektorPhoto} 
                      alt="Foto Rektor" 
                      className="img-fluid rounded"
                      style={{ width: '100%', height: 'auto' }}
                    />
                  </div>
                  <div className="col-12 col-md-8">
                    <h5 className="mb-3">Assalamualaikum Warahmatullahi Wabarakatuh</h5>
                    <p className="text-justify">
                      Dengan semangat <strong>Integrated, Green, Islamic, Futuristic (I-GIFt)</strong>, platform ini mendukung penjaminan mutu menuju tata kelola efektif, efisien, transparan dan berorientasi pada peningkatan mutu caturdharma menuju <strong>Research and International Reputable University</strong>.                      
                    </p>
                    <p className="mb-0 fw-bold">
                      Dr. Ir. H. Abd. Rakhim Nanda, S.T., M.T., IPU<br />
                      <small className="text-muted">Rektor</small>
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
          
          <div className="col-12 col-lg-6 d-flex">
            <Card className="w-100 shadow-sm">
              <CardBody>
                <div className="row align-items-center">
                  <div className="col-12 col-md-4 mb-3 mb-md-0">
                    <img 
                      src={ketuaPhoto} 
                      alt="Logo" 
                      className="img-fluid rounded"
                      style={{ width: '100%', height: 'auto' }}
                    />
                  </div>
                  <div className="col-12 col-md-8">
                    <h5 className="mb-3">Assalamualaikum Warahmatullahi Wabarakatuh</h5>
                    <p className="text-justify">
                      Selamat datang di platform resmi <strong>Badan Penjaminan Mutu Unismuh Makassar</strong>. Kami berkomitmen meningkatkan mutu pendidikan melalui sistem PPEPP, tata kelola inovatif dan islami menuju <strong>Research and International Reputable University</strong>.
                    </p>
                    <p className="mb-0 fw-bold">
                      Dr. Ir. Amrullah Mansida, S.T., M.T., IPM., Asean Eng.<br />
                      <small className="text-muted">Kepala Badan Penjamin Mutu</small>
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </section>

        {/* Baris Kedua - Card Aplikasi (Dynamic from API with Hierarchy) */}
        <section className="row mb-2 px-2 py-0">
          {loading ? (
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Memuat data aplikasi...</p>
            </div>
          ) : error ? (
            <div className="col-12">
              <div className="alert alert-warning" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            </div>
          ) : hierarchicalMenus.length === 0 ? (
            <div className="col-12 text-center py-5">
              <p className="text-muted">Tidak ada aplikasi yang tersedia saat ini.</p>
            </div>
          ) : (
            hierarchicalMenus.map((menu) => renderMenuItem(menu))
          )}
        </section>
      </div>
    </>
  );
}

export default Beranda;
